<template>
  <div>
    <b-loading :is-full-page="true" :active.sync="isLoading"> </b-loading>
    <table id="ni-ri-product-types-table">
      <thead>
        <tr>
          <th>Line No.</th>
          <th class="select-checkbox-cell">
            <b-checkbox
              v-model="selectAllChecked"
              type="is-info"
              size="is-large"
              title="Select All"
            ></b-checkbox>
          </th>
          <th
            v-for="columnData in regulatoryInfoProductTypeFields.columns"
            :key="columnData.id"
            :class="columnData.className"
            :title="columnData.title"
          >
            <label>{{ columnData.label }}</label>
          </th>
        </tr>
      </thead>
      <tbody class="ni-ri-product-types-tbody">
        <tr
          v-for="(row, index) in rows"
          :key="row.id"
          :class="[
            row.itemStatus === 'Rework – Additional Data Required'
              ? 'rework-item'
              : ''
          ]"
          @click="onRowClick($event)"
          @change="!isLoading ? changeOnRow($event, index, row) : ''"
        >
          <ValidationObserver>
            <td class="line-number">{{ row.lineNumber }}</td>
            <td class="select-checkbox-cell">
              <b-checkbox
                :disabled="showSubmissionInReadOnly"
                type="is-info"
                size="is-large"
                v-model="checkboxGroup"
                @input="emitRowIds"
                :native-value="row.genlInfoId"
              ></b-checkbox>
            </td>
            <td class="line-errors">{{ row.lineErrors }}</td>
            <td class="supp-item-nbr">
              <b-input
                class="suppItemNbr"
                v-model="row.suppItemNbr"
                disabled
              ></b-input>
            </td>
            <td>
              <b-input
                class="englishPrdctNoun"
                disabled
                :value="row.englishPrdctNoun"
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemGiErrTbs,
                    'englishPrdctNoun',
                    row.englishPrdctNoun
                  )
                "
              ></b-input>
            </td>
            <td>
              <b-input
                class="itemStatus"
                v-model="row.itemStatus"
                disabled
                :title="row.itemStatus"
              ></b-input>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    v-model="row.itemCeMarked"
                    class="itemCeMarked"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemRiPrdErrTbs,
                        'itemCeMarked',
                        row.itemCeMarked
                      )
                    "
                    ><option value="Y">Yes</option
                    ><option value="N">No</option></b-select
                  >
                </div>
              </ValidationProvider>
            </td>
            <td>
              <a
                v-if="row.ceCertificateName"
                class="ceCertificate has-text-info"
              >
                <span
                  :title="'Click to Download ' + row.ceCertificateName"
                  class="attachment-title download-image-attachment"
                  @click="
                    downloadCeCertAttach(
                      row.ceCertificateName,
                      row.nbrNewItemCrKey,
                      row.genlInfoId
                    )
                  "
                  >{{ row.ceCertificateName }} </span
                >&nbsp;&nbsp;&nbsp;<a
                  @click="deleteCeCertAttach(row.genlInfoId)"
                  class="delete-attachment"
                  title="Delete Attachment"
                >
                  <b-icon icon="delete" type="is-info" size="is-small"> </b-icon
                ></a>
              </a>
              <b-upload
                v-else
                @input="uploadCeCertAttach(row.genlInfoId)"
                class="ceCertificate upload upload-attachment"
                v-model="ceCertFile"
                :class="[
                  row.itemCeMarked === 'Y' && !row.ceCertificateName
                    ? 'invalid'
                    : '',
                  isApiErrorField(
                    row.psfsEuNewItemRiPrdErrTbs,
                    'ceCertificate',
                    row.ceCertificate
                  )
                ]"
              >
                <a class="button">
                  <span>Upload</span>
                </a>
              </b-upload>
            </td>
            <td>
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <b-select
                    v-model="row.diagnostic"
                    class="diagnostic"
                    :class="
                      isApiErrorField(
                        row.psfsEuNewItemRiPrdErrTbs,
                        'diagnostic',
                        row.diagnostic
                      )
                    "
                    ><option value="Y">Yes</option
                    ><option value="N">No</option></b-select
                  >
                </div>
              </ValidationProvider>
            </td>
            <td v-on:keypress="typeAheadTriggered = 'medDeviceClass'">
              <v-select
                :filterable="false"
                :taggable="false"
                :options="medDeviceClassOptions"
                class="medDeviceClass"
                v-bind:class="
                  isApiErrorField(
                    row.psfsEuNewItemRiPrdErrTbs,
                    'medDeviceClass',
                    row.medDeviceClass
                  )
                "
                v-model="row.medDeviceClass"
                @search="fetchTypeAhead"
                @input="changeOnRow($event, index, row)"
                :title="row.medDeviceClass"
              >
                <template slot="option" slot-scope="medDeviceClassOption">
                  <div class="d-center">
                    {{ medDeviceClassOption.label }}
                  </div>
                </template>
                <template
                  slot="selected-option"
                  slot-scope="medDeviceClassOption"
                  title="medDeviceClassOption"
                >
                  <div class="selected d-center">
                    {{ medDeviceClassOption.label }}
                  </div>
                </template>
                <template slot="no-options" slot-scope="{ search, searching }">
                  <template v-if="searching">
                    Sorry, no matching options.
                  </template>
                  <span v-else class="display-none"></span>
                </template>
              </v-select>
            </td>
            <td v-on:keypress="typeAheadTriggered = 'euEccnNbr'">
              <ValidationProvider
                rules="required"
                :bails="false"
                v-slot="{ classes }"
                immediate
              >
                <div class="control" :class="classes">
                  <v-select
                    :filterable="false"
                    :taggable="false"
                    :options="euEccnNbrOptions"
                    class="euEccnNbr"
                    v-bind:class="
                      isApiErrorField(
                        row.psfsEuNewItemRiPrdErrTbs,
                        'euEccnNbr',
                        row.euEccnNbr
                      )
                    "
                    v-model="row.euEccnNbr"
                    @search="fetchTypeAhead"
                    @input="changeOnRow($event, index, row)"
                    :title="row.euEccnNbr"
                  >
                    <template slot="option" slot-scope="euEccnNbrOption">
                      <div class="d-center">
                        {{ euEccnNbrOption.label }}
                      </div>
                    </template>
                    <template
                      slot="selected-option"
                      slot-scope="euEccnNbrOption"
                      title="euEccnNbrOption"
                    >
                      <div class="selected d-center">
                        {{ euEccnNbrOption.label }}
                      </div>
                    </template>
                    <template
                      slot="no-options"
                      slot-scope="{ search, searching }"
                    >
                      <template v-if="searching">
                        Sorry, no matching options.
                      </template>
                      <span v-else class="display-none"></span>
                    </template>
                  </v-select>
                </div>
              </ValidationProvider>
            </td>
            <td>
              <b-input
                class="usEccnNbr"
                oninput='this.value = this.value.replace(/[,"]+/g, "");if (this.value.length > 5)
                          this.value = this.value.slice(0, 5);'
                @change.native="row.usEccnNbr = $event.target.value"
                :value="row.usEccnNbr"
                :class="
                  isApiErrorField(
                    row.psfsEuNewItemRiPrdErrTbs,
                    'usEccnNbr',
                    row.usEccnNbr
                  )
                "
              ></b-input>
            </td>
          </ValidationObserver>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import vSelect from "vue-select";

export default {
  name: "RegulatoryInfoProductTypesTab",
  components: { vSelect },
  data() {
    return {
      action: [],
      checkboxGroup: [],
      ceCertFile: null,
      isLoading: false,
      paginationParams: [],
      regulatoryInfoProductTypeFields: {
        columns: [
          {
            className: "lineErrors",
            field: "lineErrors",
            label: "Line Errors",
            title: "Line Errors"
          },
          {
            className: "suppItemNbr",
            field: "suppItemNbr",
            label: "Supplier Item No.",
            title: "Supplier Item No.\nMax length is 25."
          },
          {
            className: "englishPrdctNoun",
            field: "englishPrdctNoun",
            label: "English Product Description",
            title: "Product Description"
          },
          {
            className: "itemStatus",
            field: "itemStatus",
            label: "Item Status",
            title: "Item Status"
          },
          {
            className: "itemCeMarked",
            field: "itemCeMarked",
            label: "Is item CE marked",
            title: "Y/N if Y Please provde the CE certificate."
          },
          {
            className: "ceCertificate",
            field: "ceCertificate",
            label: "CE certificate",
            title: "Attach here the CE certificate"
          },
          {
            className: "diagnostic",
            field: "diagnostic",
            label: "In Vitro Diagnostic (Y/N)",
            title: "Product could be used for In Vitro Diagnostic (Y/N)"
          },
          {
            className: "medDeviceClass",
            field: "medDeviceClass",
            label: "Medical Devices Classification",
            title: "Options, I, IIa, IIb, III, Other"
          },
          {
            className: "euEccnNbr",
            field: "euEccnNbr",
            label: "EU ECCN Number",
            title: "Supply valid number (alpha-numeric)"
          },
          {
            className: "usEccnNbr",
            field: "usEccnNbr",
            label: "US ECCN Number (If applicable)",
            title: "Supply valid number (alpha-numeric)"
          }
        ]
      },
      rowIds: [],
      rows: [],
      showSubmissionInReadOnly: false,
      selectAllChecked: false,
      euEccnNbrOptions: [],
      medDeviceClassOptions: []
    };
  },
  props: ["riPtTabSubmissionID", "loadRiPtTabData", "resetCheckboxSelection"],
  watch: {
    loadRiPtTabData(value) {
      if (value.loadDetails) {
        this.actions = value.actions;
        this.paginationParams = value.paginationParams;
        this.loadTabItemDetails();
      }
    },
    selectAllChecked: function(isChecked) {
      if (isChecked) {
        this.checkboxGroup = this.rowIds;
      } else {
        this.checkboxGroup = [];
      }
      this.emitRowIds();
    },
    resetCheckboxSelection() {
      this.checkboxGroup = [];
    }
  },
  mounted() {},
  methods: {
    changeOnRow(event, index, rowData) {
      var rowArrayFromResponse = JSON.parse(
        sessionStorage.getItem("riPtEuRows")
      );
      if (rowArrayFromResponse && rowArrayFromResponse.length > 0) {
        var rowDataFromResponseObj = JSON.parse(
          sessionStorage.getItem("riPtEuRows")
        )[index];
        for (var key in (rowDataFromResponseObj, rowData)) {
          if (
            key !== "psfsEuNewItemRiPrdErrTbs" &&
            key !== "lineNumber" &&
            rowDataFromResponseObj[key] !== rowData[key]
          ) {
            if (
              !(
                rowDataFromResponseObj[key] === undefined &&
                rowData[key] === " "
              )
            ) {
              if (rowData[key] === " ") {
                rowData[key] = "";
              }
              this.updateRiPtItem(rowData, index);
              break;
            }
          }
        }
      }
    },
    deleteCeCertAttach(genlInfoId) {
      var scope = this;
      scope.isLoading = true;
      scope
        .$http({
          url: `${process.env.VUE_APP_API_BASE_DOMAIN}eu/deleteAttachment?genlInfoId=${genlInfoId}&attachmentName=ceCertificate`,
          method: "delete"
        })
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `Delete CE certificate action successful.`,
            confirmText: "OK",
            type: "is-success",
            onConfirm: () => scope.loadTabItemDetails()
          });
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occured with delete action. Please clear the cache and try again. If the problems persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    downloadCeCertAttach(ceCertificateName, nbrNewItemCrKey, genlInfoId) {
      var scope = this;
      scope.isLoading = true;
      scope
        .$http({
          url: `${process.env.VUE_APP_API_BASE_DOMAIN}eu/downloadAttachment?nbrNewItemCrKey=${nbrNewItemCrKey}&attachmentName=ceCertificate&documentName=${ceCertificateName}&genlInfoId=${genlInfoId}`,
          method: "GET",
          responseType: "blob"
        })
        .then(function(response) {
          scope.isLoading = false;
          var url = window.URL.createObjectURL(new Blob([response.data]));
          var link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", ceCertificateName);
          document.body.appendChild(link);
          link.click();
        })
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.dialog.alert({
            message: `A problem occurred with CE certificate download. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            confirmText: "OK",
            type: "is-danger"
          });
        });
    },
    emitRowIds() {
      this.$emit("riPtIdsForUpload", this.checkboxGroup);
    },
    fetchTypeAhead(search, loading) {
      if (search.length > 0) {
        var scope = this;

        loading(true);
        scope.$http
          .get(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/loadTypeaheadsCodes?fieldName=${scope.typeAheadTriggered}&code=${search}`
          )
          .then(function(response) {
            loading(false);
            switch (scope.typeAheadTriggered) {
              case "euEccnNbr":
                scope.euEccnNbrOptions = response.data;
                break;
              case "medDeviceClass":
                scope.medDeviceClassOptions = response.data;
                break;
            }
          })
          // eslint-disable-next-line no-unused-vars
          .catch(function(error) {
            loading(false);
            scope.$buefy.dialog.alert({
              message: `Failed to load matching results. Please enter valid search criteria and/or clear the cache and try again. If the problem persists, please contact the New Item Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger"
            });
          });
      }
    },
    // eslint-disable-next-line no-unused-vars
    isApiErrorField(errorFieldNames, className, value) {
      if (errorFieldNames && errorFieldNames.length > 0) {
        for (var i = 0; i < errorFieldNames.length; i++) {
          if (
            errorFieldNames[i].columnName
              .split(" ")[0]
              .replace(/_/g, "")
              .toLowerCase() === className.toLowerCase()
          ) {
            return "api-error";
          }
        }
      }
    },
    onRowClick(event) {
      var tableRows = event.target.closest("tr").parentElement.rows;
      for (var row in tableRows) {
        if (
          tableRows[row].classList &&
          tableRows[row].classList.contains("highlight-row")
        ) {
          tableRows[row].classList.remove("highlight-row");
        }
      }
      event.target.closest("tr").classList.add("highlight-row");
    },
    loadTabItemDetails() {
      var scope = this;
      scope.isLoading = true;
      var args = {
        headers: {
          "content-type": "application/json"
        }
      };

      var requestPayload = {};
      requestPayload.actions = [];
      requestPayload.filterMap = {};
      requestPayload.searchMap = {};
      requestPayload.sortMap = {};
      if (
        this.actions.fErrors ||
        this.actions.fSuppItemNbr ||
        this.actions.fProductNoun ||
        this.actions.fCeMarked ||
        this.actions.fReworkItem
      ) {
        requestPayload.actions.push("Filter");
        requestPayload.filterMap = {};
        if (this.actions.fErrors) {
          requestPayload.filterMap.ErroredItems = this.actions.fErrors;
        }
        if (this.actions.fSuppItemNbr) {
          requestPayload.filterMap.suppItemNbr = this.actions.fSuppItemNbr;
        }
        if (this.actions.fProductNoun) {
          requestPayload.filterMap.englishPrdctNoun = this.actions.fProductNoun;
        }
        if (this.actions.fCeMarked) {
          requestPayload.filterMap.itemCeMarked = this.actions.fCeMarked;
        }
        if (this.actions.fReworkItem) {
          requestPayload.filterMap.itemStatus = "K";
        }
      }
      if (this.actions.sortSuppItemNbr) {
        requestPayload.actions.push("Sort");
        requestPayload.sortMap = {};
        requestPayload.sortMap.suppItemNbr = this.actions.sortSuppItemNbr;
      }
      if (this.actions.srFieldName && this.actions.srFieldValue) {
        requestPayload.actions.push("Search");
        requestPayload.searchMap = {};
        requestPayload.searchMap[
          this.actions.srFieldName
        ] = this.actions.srFieldValue;
      }

      scope.$http
        .post(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/getRiProductInfo?nbrNewItemCrKey=${scope.riPtTabSubmissionID}&pageIndex=${scope.paginationParams.pageIndex}&noOfRecords=${scope.paginationParams.numberOfItems}`,
          requestPayload,
          args
        )
        .then(function(response) {
          var riPtPaginationParams = {
            startIndex: response.data.startIndex,
            endIndex: response.data.endIndex,
            totalCount: response.data.totalCount,
            totalPages: response.data.totalPages
          };
          scope.$emit("riPtPaginationParams", riPtPaginationParams);
          scope.$emit("riptCount", {
            totalErrorCount: response.data.errorCount,
            tabErrorCount: response.data.riProdTabErrorCount
          });

          //check supplier validate value
          if (sessionStorage.getItem("se_ni_user_eu")) {
            if (response.data.validateForm === "Y") {
              scope.$emit("disableSupplierValidateFromTab", true);
            } else {
              scope.$emit("enableSupplierValidateFromTab", true);
            }
          }

          scope.isLoading = false;
          scope.rows = [];
          scope.rowIds = [];
          var mutableResponseRows = response.data.psfsEuNewItemRiPrdctTbs;
          if (requestPayload.actions.length > 0) {
            if (response.data.psfsEuNewItemRiPrdctTbs.length > 0) {
              for (
                var i = 0, lineNumber = riPtPaginationParams.startIndex;
                i < mutableResponseRows.length && lineNumber > 0;
                i++, lineNumber++
              ) {
                scope.rowIds.push(mutableResponseRows[i].genlInfoId);
                mutableResponseRows[i].lineNumber = lineNumber;
              }
              scope.rows = mutableResponseRows;
              sessionStorage.setItem(
                "riPtEuRows",
                JSON.stringify(response.data.psfsEuNewItemRiPrdctTbs)
              );
            } else {
              scope.rows = [];
              scope.rowIds = [];
            }
          } else {
            if (response.data.psfsEuNewItemRiPrdctTbs.length > 0) {
              for (
                var j = 0, lineNumberJ = riPtPaginationParams.startIndex;
                j < mutableResponseRows.length && lineNumberJ > 0;
                j++, lineNumberJ++
              ) {
                scope.rowIds.push(mutableResponseRows[j].genlInfoId);
                mutableResponseRows[j].lineNumber = lineNumberJ;
              }
              scope.rows = mutableResponseRows;
              sessionStorage.setItem(
                "riPtEuRows",
                JSON.stringify(response.data.psfsEuNewItemRiPrdctTbs)
              );
            } else {
              scope.rows = [];
              scope.rowIds = [];
              sessionStorage.removeItem("rows");
            }
          }
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.snackbar.open({
            duration: 5000,
            message: `Failed to load items for Submission - ${scope.riPtTabSubmissionID}. Please clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            type: "is-danger",
            position: "is-top",
            queue: false
          });
        });
    },
    updateRiPtItem(requestPayload, index) {
      var scope = this;
      var args = {
        headers: {
          "content-type": "application/json"
        }
      };

      if (sessionStorage.getItem("se_ni_user_eu")) {
        requestPayload.createdBy = JSON.parse(
          sessionStorage.getItem("se_ni_user_eu")
        ).networkId;
      } else {
        requestPayload.createdBy = window.$cookies.get("ni_key").networkId;
      }

      var updatedRowData = {};

      for (var key in requestPayload) {
        var prop = "";
        prop = requestPayload[key];
        if (typeof prop === "string") {
          if (prop.trim() !== "") {
            updatedRowData[key] = requestPayload[key];
          }
        } else {
          updatedRowData[key] = requestPayload[key];
        }
      }

      scope.isLoading = true;
      scope.$http
        .put(
          `${process.env.VUE_APP_API_BASE_DOMAIN}eu/updateRiProdInfo?nbrNewItemCrKey=${scope.riPtTabSubmissionID}`,
          updatedRowData,
          args
        )
        // eslint-disable-next-line no-unused-vars
        .then(function(response) {
          scope.isLoading = false;
          var apiRow = response.data;
          apiRow.lineNumber = scope.rows[index].lineNumber;
          scope.rows[index] = apiRow;
          var riPtEuRows = JSON.parse(sessionStorage.getItem("riPtEuRows"));
          riPtEuRows.splice(index, 1, response.data);
          sessionStorage.setItem("riPtEuRows", JSON.stringify(riPtEuRows));
          scope.$emit("riptCount", {
            totalErrorCount: response.data.errorCount,
            tabErrorCount: response.data.riProdTabErrorCount
          });
        })
        // eslint-disable-next-line no-unused-vars
        .catch(function(error) {
          scope.isLoading = false;
          scope.$buefy.snackbar.open({
            duration: 5000,
            message: `Failed to update item information for Submission - ${scope.riPtTabSubmissionID}. Please check for invalid data and/or clear the cache and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
            type: "is-danger",
            position: "is-top",
            queue: false
          });
        })
        .then(function() {
          var tableRows = document.querySelector(".ni-ri-product-types-tbody")
            .rows;
          for (var row in tableRows) {
            if (
              tableRows[row].classList &&
              tableRows[row].classList.contains("highlight-row")
            ) {
              tableRows[row].classList.remove("highlight-row");
            }
          }
          //add highlight-row class
          document
            .querySelector(".ni-ri-product-types-tbody")
            .rows[index].classList.add("highlight-row");
          //blur select event on active element wrt index
          if (
            document.activeElement.closest("tr") &&
            document.activeElement.closest("tr").rowIndex !== index + 1
          ) {
            document.activeElement.blur();
          }
        });
    },
    uploadCeCertAttach(genlInfoId) {
      var scope = this;
      var isUploadValid = /^.*\.(pdf|xls|xlsx)$/.test(
        this.ceCertFile.name.toLowerCase()
      );
      if (isUploadValid) {
        var args = {
          headers: {
            "content-type": "form-data"
          }
        };
        var uploadData = new FormData();
        uploadData.append("ceCertificate", scope.ceCertFile);

        scope.isLoading = true;
        scope.$http
          .post(
            `${process.env.VUE_APP_API_BASE_DOMAIN}eu/uploadItemLevelAttachments?genlInfoId=${genlInfoId}`,
            uploadData,
            args
          )
          // eslint-disable-next-line no-unused-vars
          .then(function(response) {
            scope.isLoading = false;
            scope.ceCertificate = null;
            scope.$buefy.dialog.alert({
              message: `CE certificate upload successful.`,
              confirmText: "OK",
              type: "is-success",
              onConfirm: () => scope.loadTabItemDetails()
            });
          })
          .catch(function(error) {
            scope.isLoading = false;
            scope.$buefy.dialog.alert({
              message: `A problem occurred with the attachment used. Please ensure attachment has the one of the following extensions: pdf, png, jpg, eps, tiff and try again. If the problem persists, please contact the IT Support at NewItem.IT.Support@thermofisher.com`,
              confirmText: "OK",
              type: "is-danger",
              onConfirm: () => (scope.ceCertificate = null)
            });
          });
      } else {
        scope.$buefy.dialog.alert({
          message: `"${scope.ceCertFile.name}" - Invalid file type, system accepts only pdf, xls, xlsx format.`,
          confirmText: "OK",
          type: "is-danger",
          onConfirm: () => (scope.ceCertificate = null)
        });
      }
    }
  }
};
</script>
